/**
 * JOC Club Tier Configuration
 */

export enum JocClubTier {
  NONE = 'none',
  BRONZE = 'bronze',
  SILVER = 'silver',
  GOLD = 'gold',
  PLATINUM = 'platinum',
}

export interface TierConfig {
  tier: JocClubTier;
  name: string;
  nameJa: string;
  minJoc: number;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
}

export const TIER_CONFIGS: Record<JocClubTier, TierConfig> = {
  [JocClubTier.NONE]: {
    tier: JocClubTier.NONE,
    name: 'Not a Member',
    nameJa: '未加入',
    minJoc: 0,
    icon: '',
    color: 'text-muted-foreground',
    bgColor: 'bg-muted',
    borderColor: 'border-muted',
  },
  [JocClubTier.BRONZE]: {
    tier: JocClubTier.BRONZE,
    name: 'Bronze',
    nameJa: 'ブロンズ',
    minJoc: 1_000,
    icon: '🥉',
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    borderColor: 'border-amber-300',
  },
  [JocClubTier.SILVER]: {
    tier: JocClubTier.SILVER,
    name: 'Silver',
    nameJa: 'シルバー',
    minJoc: 5_000,
    icon: '🥈',
    color: 'text-slate-500',
    bgColor: 'bg-slate-100',
    borderColor: 'border-slate-300',
  },
  [JocClubTier.GOLD]: {
    tier: JocClubTier.GOLD,
    name: 'Gold',
    nameJa: 'ゴールド',
    minJoc: 50_000,
    icon: '🥇',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100',
    borderColor: 'border-yellow-400',
  },
  [JocClubTier.PLATINUM]: {
    tier: JocClubTier.PLATINUM,
    name: 'Platinum',
    nameJa: 'プラチナ',
    minJoc: 1_000_000,
    icon: '💎',
    color: 'text-cyan-600',
    bgColor: 'bg-gradient-to-r from-cyan-100 to-purple-100',
    borderColor: 'border-cyan-400',
  },
};

// Ordered tiers from lowest to highest
export const TIER_ORDER = [
  JocClubTier.NONE,
  JocClubTier.BRONZE,
  JocClubTier.SILVER,
  JocClubTier.GOLD,
  JocClubTier.PLATINUM,
];

/**
 * Get the tier for a given JOC balance
 */
export function getTierFromBalance(balanceWei: bigint | undefined): JocClubTier {
  if (!balanceWei) return JocClubTier.NONE;

  // Convert from wei (18 decimals) to JOC
  const balanceJoc = Number(balanceWei / BigInt(10 ** 18));

  if (balanceJoc >= TIER_CONFIGS[JocClubTier.PLATINUM].minJoc) {
    return JocClubTier.PLATINUM;
  }
  if (balanceJoc >= TIER_CONFIGS[JocClubTier.GOLD].minJoc) {
    return JocClubTier.GOLD;
  }
  if (balanceJoc >= TIER_CONFIGS[JocClubTier.SILVER].minJoc) {
    return JocClubTier.SILVER;
  }
  if (balanceJoc >= TIER_CONFIGS[JocClubTier.BRONZE].minJoc) {
    return JocClubTier.BRONZE;
  }
  return JocClubTier.NONE;
}

/**
 * Get the next tier after the current one
 */
export function getNextTier(currentTier: JocClubTier): JocClubTier | null {
  const currentIndex = TIER_ORDER.indexOf(currentTier);
  if (currentIndex === -1 || currentIndex === TIER_ORDER.length - 1) {
    return null;
  }
  return TIER_ORDER[currentIndex + 1];
}

/**
 * Calculate progress to the next tier
 */
export function getTierProgress(balanceWei: bigint | undefined): {
  currentTier: JocClubTier;
  nextTier: JocClubTier | null;
  currentJoc: number;
  nextTierMinJoc: number;
  progress: number; // 0-100
  remaining: number;
} {
  const balanceJoc = balanceWei ? Number(balanceWei / BigInt(10 ** 18)) : 0;
  const currentTier = getTierFromBalance(balanceWei);
  const nextTier = getNextTier(currentTier);

  if (!nextTier) {
    // Already at Platinum
    return {
      currentTier,
      nextTier: null,
      currentJoc: balanceJoc,
      nextTierMinJoc: TIER_CONFIGS[JocClubTier.PLATINUM].minJoc,
      progress: 100,
      remaining: 0,
    };
  }

  const currentTierMin = TIER_CONFIGS[currentTier].minJoc;
  const nextTierMin = TIER_CONFIGS[nextTier].minJoc;
  const range = nextTierMin - currentTierMin;
  const progressInRange = balanceJoc - currentTierMin;
  const progress = Math.min(100, Math.max(0, (progressInRange / range) * 100));
  const remaining = nextTierMin - balanceJoc;

  return {
    currentTier,
    nextTier,
    currentJoc: balanceJoc,
    nextTierMinJoc: nextTierMin,
    progress,
    remaining: Math.max(0, remaining),
  };
}

/**
 * Format JOC amount with K/M suffix
 */
export function formatJocAmount(amount: number): string {
  if (amount >= 1_000_000) {
    return `${(amount / 1_000_000).toFixed(1)}M`;
  }
  if (amount >= 1_000) {
    return `${(amount / 1_000).toFixed(0)}K`;
  }
  return amount.toLocaleString();
}
